import type { ScaleOrdinal, Series, SeriesPoint } from 'd3';
import { scaleOrdinal, stack } from 'd3';

import { type CommonPlotProps, FormatType } from '../../types';
import { getFormatter, getOtherColor } from '../../utilities';
import { createCombinedLegendFormatter } from '../../utilities/create-combined-legend-formatter';
import type { BarData } from '../bar/bar-chart';
import { Legend } from '../legend/legend';
import { stackedSegmentShare, stackedSegmentValue } from './stacked-bar-scale';

export const Dimensions = {
  LabelPadding: 5,
  MaxBarThickness: 40,
  MinBarThickness: 4,
  BandPaddingFactor: 0.2,
  SegmentSpacing: 2,
  LegendHeight: 42,
  XAxisLabelHeight: 28,
  NewBandPaddingInner: 0.54,
  NewBandPaddingOuter: 0.2,
  NewSegmentSpacing: 3,
} as const;

export type StackedBarOrientation = 'horizontal' | 'vertical';

export type StackedBarData<TData extends object = object> = {
  group: string;
  values: BarData<TData>[];
};

export type StackedBarDataIndexed = {
  label: string;
  segments: Record<string, number>;
  _index: number;
};

export type StackedBarChartProps = {
  data: StackedBarData[];
  format?: FormatType;
  colorOverride?: Record<string, string>;
  colorPalette?: string[];
  hideLegend?: boolean;
  formatLegendLabel?: (label: string) => string;
  maxLabelWidth?: number;
  maxLegendLabelWidth?: number;
  segmentOrder?: string[];
  normalized?: boolean;
  orientation?: StackedBarOrientation;
  isTimeseries?: boolean;
  highlightMatchingSegments?: boolean;
} & CommonPlotProps;

type StackedBarModel = {
  dataIndexed: StackedBarDataIndexed[];
  displayLabels: string[];
  segmentLabels: string[];
  stackedSeries: Series<StackedBarDataIndexed, string>[];
  emptyBars: StackedBarDataIndexed[];
  colorScale: ScaleOrdinal<string, string, string>;
  domainMax: number;
  noData: boolean;
};

export type StackedBarRendererProps = {
  model: StackedBarModel;
  width: number;
  height: number;
  format: FormatType;
  hideLegend?: boolean;
  maxLabelWidth: number;
  formatLegendLabel: (label: string) => string;
  maxLegendLabelWidth?: number;
  isTimeseries?: boolean;
  highlightMatchingSegments?: boolean;
};

type ProcessOptions = {
  segmentOrder?: string[];
  normalized: boolean;
  colorOverride?: Record<string, string>;
  colorPalette: string[];
};

export const processStackedBarData = (
  data: StackedBarData[],
  { segmentOrder, normalized, colorOverride, colorPalette }: ProcessOptions
): StackedBarModel => {
  /* Flatten nested values into the internal segment record. */
  const dataIndexed: StackedBarDataIndexed[] = data.map((bar, idx) => ({
    label: bar.group,
    segments: bar.values.reduce<Record<string, number>>(
      (acc, v) => ({ ...acc, [v.label]: v.value ?? 0 }),
      {}
    ),
    _index: idx,
  }));

  const labelCounts = new Map<string, number>();
  const displayLabels = dataIndexed.map((bar) => {
    const count = labelCounts.get(bar.label) ?? 0;
    labelCounts.set(bar.label, count + 1);
    return count === 0 ? bar.label : `${bar.label} (${count + 1})`;
  });

  const observedSegmentLabels = Array.from(
    new Set(data.flatMap((bar) => bar.values.map((v) => v.label)))
  );
  const segmentLabels = (() => {
    if (segmentOrder === undefined) return observedSegmentLabels;
    const orderedKnown = segmentOrder.filter((label) =>
      observedSegmentLabels.includes(label)
    );
    const extras = observedSegmentLabels.filter(
      (label) => !segmentOrder.includes(label)
    );
    return [...orderedKnown, ...extras];
  })();

  const emptyBars = dataIndexed.filter((bar) =>
    Object.values(bar.segments).every((value) => value === 0)
  );

  const stackedSeries = stack<StackedBarDataIndexed>()
    .keys(segmentLabels)
    .value((d, key) => stackedSegmentValue(d.segments, key, normalized))(
    dataIndexed
  );

  const overridenColors = Object.values(colorOverride ?? {});
  const colors = colorPalette.filter(
    (color) => !overridenColors.includes(color)
  );
  const labelColors = segmentLabels.map((label, index) => ({
    label,
    color: colorOverride?.[label] ?? colors[index] ?? getOtherColor(),
  }));
  const colorScale = scaleOrdinal<string>()
    .domain(labelColors.map(({ label }) => label))
    .range(labelColors.map(({ color }) => color))
    .unknown(getOtherColor());

  const noData =
    data.length === 0 ||
    data.every((metric) => metric.values.length === 0) ||
    data.every((metric) =>
      metric.values.every((value) => (value.value ?? 0) === 0)
    );

  return {
    dataIndexed,
    displayLabels,
    segmentLabels,
    stackedSeries,
    emptyBars,
    colorScale,
    domainMax: normalized
      ? 1
      : Math.max(
          1,
          ...data.map((bar) =>
            bar.values.reduce((acc, v) => acc + (v.value ?? 0), 0)
          )
        ),
    noData,
  };
};

export const buildSegmentEntries = (
  series: Series<StackedBarDataIndexed, string>,
  active: SeriesPoint<StackedBarDataIndexed>,
  format: FormatType,
  focusHoveredOnly = false
) => {
  const formatMain = getFormatter(format);
  const formatSecondary = getFormatter(FormatType.PERCENTAGE);
  const entries = series.map((s) => ({
    active: s.data.label === active.data.label,
    label: s.data.label,
    mainValue: formatMain(s.data.segments[series.key] ?? 0),
    secondaryValue: formatSecondary(
      stackedSegmentShare(s.data.segments, series.key)
    ),
  }));
  return focusHoveredOnly ? entries.filter((entry) => entry.active) : entries;
};

type StackedBarLegendProps = {
  segmentLabels: string[];
  colorScale: ScaleOrdinal<string, string, string>;
  formatLegendLabel: (label: string) => string;
  maxLegendLabelWidth?: number;
  marginLeft?: number;
  className?: string;
};

export const StackedBarLegend = ({
  segmentLabels,
  colorScale,
  formatLegendLabel,
  maxLegendLabelWidth,
  marginLeft,
  className,
}: StackedBarLegendProps) => {
  const combinedLegendFormatter = createCombinedLegendFormatter(
    formatLegendLabel,
    maxLegendLabelWidth
  );

  return (
    <div
      className={className}
      style={{
        maxHeight: Dimensions.LegendHeight,
        ...(marginLeft !== undefined && { marginLeft: `${marginLeft}px` }),
      }}
    >
      <Legend
        items={segmentLabels.map((label) => ({
          label,
          color: colorScale(label),
        }))}
        formatLabel={combinedLegendFormatter}
      />
    </div>
  );
};
